/**
 * Bancos - Finance 360
 * Bank accounts list and management
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Building2,
  Plus,
  CreditCard,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Upload,
  Settings,
  CheckCircle2,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { contabilidadApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import LoadingSpinner, { LoadingPage } from '../../components/LoadingSpinner';

// Toolbar button component
function ToolbarButton({ icon: Icon, label, onClick, disabled, variant = 'default' }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <Icon className="h-5 w-5 mb-1" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

function ToolbarSeparator() {
  return <div className="w-px h-12 bg-gray-300 mx-1" />;
}

export default function Bancos() {
  const { currentEmisor } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [cuentas, setCuentas] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCuenta, setEditingCuenta] = useState(null);
  const [planCuentas, setPlanCuentas] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    banco: '',
    tipoCuenta: 'corriente',
    numeroCuenta: '',
    moneda: 'CRC',
    cuentaContableId: ''
  });

  useEffect(() => {
    if (currentEmisor) {
      loadData();
    }
  }, [currentEmisor]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cuentasRes, planRes] = await Promise.all([
        contabilidadApi.getBancos(),
        contabilidadApi.getCuentas({ flat: true, tipo: 'activo' })
      ]);
      setCuentas(cuentasRes.data.data);
      setPlanCuentas(planRes.data.data.filter(c => c.codigo.startsWith('1.1.01')));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCuenta) {
        await contabilidadApi.updateBanco(editingCuenta.id, formData);
      } else {
        await contabilidadApi.createBanco(formData);
      }
      setShowModal(false);
      setEditingCuenta(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      banco: '',
      tipoCuenta: 'corriente',
      numeroCuenta: '',
      moneda: 'CRC',
      cuentaContableId: ''
    });
  };

  const openEdit = (cuenta) => {
    setEditingCuenta(cuenta);
    setFormData({
      nombre: cuenta.nombre,
      banco: cuenta.banco,
      tipoCuenta: cuenta.tipoCuenta,
      numeroCuenta: cuenta.numeroCuenta || '',
      moneda: cuenta.moneda,
      cuentaContableId: cuenta.cuentaContableId
    });
    setShowModal(true);
  };

  const formatCurrency = (amount, moneda = 'CRC') => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: moneda,
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white rounded-lg border p-3 flex items-center justify-center gap-2 flex-wrap">
        <ToolbarButton
          icon={Plus}
          label="Nueva Cuenta"
          onClick={() => { resetForm(); setShowModal(true); }}
          variant="primary"
        />

        <ToolbarSeparator />

        <ToolbarButton
          icon={RefreshCw}
          label="Actualizar"
          onClick={loadData}
          disabled={loading}
        />
      </div>

      {/* Account Cards */}
      {cuentas.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-medium text-gray-900">No hay cuentas bancarias</h3>
          <p className="text-sm text-gray-500 mt-1">Agregue su primera cuenta bancaria para comenzar</p>
          <button
            onClick={() => setShowModal(true)}
            className="mt-4 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
          >
            Agregar Cuenta
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cuentas.map((cuenta) => {
            const diferencia = (cuenta.saldoBanco || 0) - (cuenta.saldoActual || 0);
            const pendientes = cuenta.movimientosPendientes || 0;

            return (
              <div key={cuenta.id} className="bg-white rounded-xl border hover:shadow-md transition-shadow">
                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                        cuenta.moneda === 'USD' ? 'bg-green-100' : 'bg-blue-100'
                      }`}>
                        {cuenta.moneda === 'USD' ? (
                          <DollarSign className="h-5 w-5 text-green-600" />
                        ) : (
                          <CreditCard className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{cuenta.nombre}</h3>
                        <p className="text-xs text-gray-500">{cuenta.banco}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => openEdit(cuenta)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Saldo Libros</span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(cuenta.saldoActual, cuenta.moneda)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Saldo Banco</span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(cuenta.saldoBanco, cuenta.moneda)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-sm text-gray-500">Diferencia</span>
                      <span className={`font-medium ${
                        Math.abs(diferencia) < 1 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(diferencia, cuenta.moneda)}
                      </span>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="mt-4 pt-4 border-t">
                    {pendientes > 0 ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-yellow-600">
                          <Clock className="h-4 w-4" />
                          <span className="text-sm">{pendientes} pendientes</span>
                        </div>
                        <Link
                          to={`/contabilidad/bancos/${cuenta.id}/conciliacion`}
                          className="text-sm text-blue-600 hover:text-blue-700"
                        >
                          Conciliar
                        </Link>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle2 className="h-4 w-4" />
                        <span className="text-sm">Conciliado</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="px-5 py-3 bg-gray-50 border-t rounded-b-xl flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    {cuenta.numeroCuenta ? `****${cuenta.numeroCuenta.slice(-4)}` : 'Sin número'}
                  </span>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/contabilidad/bancos/${cuenta.id}/conciliacion`}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      Importar
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingCuenta ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de la Cuenta
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: BAC Colones Principal"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
                  <select
                    value={formData.banco}
                    onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Seleccione...</option>
                    <option value="BAC San José">BAC San José</option>
                    <option value="Banco Nacional">Banco Nacional</option>
                    <option value="Banco de Costa Rica">Banco de Costa Rica</option>
                    <option value="Scotiabank">Scotiabank</option>
                    <option value="Banco Promerica">Banco Promerica</option>
                    <option value="Banco Popular">Banco Popular</option>
                    <option value="Davivienda">Davivienda</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={formData.tipoCuenta}
                    onChange={(e) => setFormData({ ...formData, tipoCuenta: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="corriente">Corriente</option>
                    <option value="ahorro">Ahorro</option>
                    <option value="inversion">Inversión</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número de Cuenta
                  </label>
                  <input
                    type="text"
                    value={formData.numeroCuenta}
                    onChange={(e) => setFormData({ ...formData, numeroCuenta: e.target.value })}
                    placeholder="Opcional"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
                  <select
                    value={formData.moneda}
                    onChange={(e) => setFormData({ ...formData, moneda: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="CRC">Colones (CRC)</option>
                    <option value="USD">Dólares (USD)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cuenta Contable
                </label>
                <select
                  value={formData.cuentaContableId}
                  onChange={(e) => setFormData({ ...formData, cuentaContableId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Seleccione cuenta contable...</option>
                  {planCuentas.map((cuenta) => (
                    <option key={cuenta.id} value={cuenta.id}>
                      {cuenta.codigo} - {cuenta.nombre}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Cuenta del plan contable donde se registrarán los movimientos
                </p>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingCuenta(null); }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
                >
                  {editingCuenta ? 'Guardar Cambios' : 'Crear Cuenta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
